// Paste sanitization + smart-paste behaviors.
//
// Browsers hand off arbitrary HTML when the user pastes from rich
// sources (Google Docs, Microsoft Word, Notion, etc.) — fonts,
// colors, inline styles, scripts, MS Office namespaces. Dropping
// that straight into our contenteditable both breaks the markdown
// round-trip and is a XSS surface. We accept ONLY the tags +
// attributes the editor itself emits.
//
// Beyond sanitization, this module classifies the clipboard payload
// so the editor can do the Slack-style smart-paste behaviors:
//   - URL pasted while a selection is active → wrap selection in <a>.
//   - URL pasted with a collapsed caret → insert as an autolinked <a>.
//   - Plain-text paragraphs with URLs → autolink each URL inline.
//   - Image bytes on the clipboard → fired up to the host so it can
//     route through the attachment pipeline.

import { detectLanguage } from '../markdown/detectLanguage';

const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'P',
  'DIV',
  'BR',
  'STRONG',
  'B',
  'EM',
  'I',
  'S',
  'STRIKE',
  'DEL',
  'CODE',
  'PRE',
  'BLOCKQUOTE',
  'UL',
  'OL',
  'LI',
  'A',
]);

const ALLOWED_ATTRS: Readonly<Record<string, ReadonlyArray<string>>> = {
  A: ['href'],
  // Preserve a code block's language label from a clean rich paste.
  PRE: ['data-lang'],
};

const SAFE_HREF_SCHEMES: ReadonlyArray<string> = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Run an HTML string through a tag/attribute allowlist. Returns a
 * sanitized HTML string safe to `insertHTML` into the editor.
 *
 * Pasted plain text is honored verbatim; the caller is responsible
 * for the text-vs-HTML decision (we hand it whichever the user's
 * `ClipboardEvent` exposed).
 *
 * Before the allowlist walks the DOM we run a normalization pass
 * that rewrites style-based formatting (the way Google Docs, Word,
 * Notion, and Slack export bold/italic/strike) into semantic tags
 * the allowlist already preserves — so a paste from those sources
 * keeps its formatting instead of collapsing to plain text.
 */
export function sanitizeHtml(dirty: string): string {
  // Parse in a detached document so any `<script>` / `<img onload>`
  // / etc. don't execute as a side effect.
  const doc = new DOMParser().parseFromString(dirty, 'text/html');
  unwrapGoogleDocsWrapper(doc.body);
  normalizeStyleFormatting(doc.body);
  flattenHeadings(doc.body);
  walk(doc.body);
  return doc.body.innerHTML;
}

/**
 * Google Docs wraps its clipboard payload in `<b style="font-weight:
 * normal">…</b>` — a no-op visually but crashes our `<b>` → bold
 * conversion. Unwrap that root-level fake-bold container before
 * doing anything else.
 */
function unwrapGoogleDocsWrapper(parent: HTMLElement): void {
  for (const child of Array.from(parent.children)) {
    if (
      (child.tagName === 'B' || child.tagName === 'STRONG') &&
      child.getAttribute('id')?.startsWith('docs-internal-guid') === true
    ) {
      while (child.firstChild !== null) {
        parent.insertBefore(child.firstChild, child);
      }
      parent.removeChild(child);
      continue;
    }
    // Also catch the `font-weight: normal` pattern without the id.
    if (child.tagName === 'B') {
      const style = (child as HTMLElement).getAttribute('style') ?? '';
      if (/font-weight\s*:\s*normal/i.test(style)) {
        while (child.firstChild !== null) {
          parent.insertBefore(child.firstChild, child);
        }
        parent.removeChild(child);
      }
    }
  }
}

/**
 * Walk the body and wrap inline-styled runs in semantic tags so the
 * allowlist preserves them. Rules:
 *
 *   font-weight: bold|600|700|800|900       → wrap in <strong>
 *   font-style: italic                       → wrap in <em>
 *   text-decoration[*]: line-through         → wrap in <s>
 *   font-family: monospace / Consolas / …    → wrap in <code>
 *
 * Multiple flags on one element compose ("bold italic" → <strong><em>).
 */
function normalizeStyleFormatting(root: HTMLElement): void {
  // Snapshot so we can mutate as we go.
  const all = Array.from(root.querySelectorAll<HTMLElement>('[style]'));
  for (const el of all) {
    const style = el.getAttribute('style') ?? '';
    if (style.length === 0) continue;

    const tags: string[] = [];
    if (/font-weight\s*:\s*(bold|[6-9]00)/i.test(style)) tags.push('strong');
    if (/font-style\s*:\s*italic/i.test(style)) tags.push('em');
    if (/text-decoration[^:]*:[^;]*line-through/i.test(style)) tags.push('s');
    if (
      /font-family\s*:\s*[^;]*(monospace|consolas|menlo|courier|sf mono|sfmono)/i.test(
        style,
      )
    ) {
      tags.push('code');
    }
    if (tags.length === 0) continue;

    // Wrap the element's contents in the stack of semantic tags. The
    // element itself stays in place (so its block role survives) but
    // its children get wrapped.
    const doc = el.ownerDocument;
    let inner: Node = doc.createDocumentFragment();
    while (el.firstChild !== null) inner.appendChild(el.firstChild);
    for (const tag of tags) {
      const wrap = doc.createElement(tag);
      wrap.appendChild(inner);
      inner = wrap;
    }
    el.appendChild(inner);
  }
}

/**
 * `<h1>…<h6>` → `<p><strong>…</strong></p>`. The editor doesn't
 * model headings (the wire format is mrkdwn-ish), but visually
 * Slack-style we still want them to read as emphasized lines after
 * paste — not as plain paragraphs.
 */
function flattenHeadings(root: HTMLElement): void {
  const headings = Array.from(
    root.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6'),
  );
  for (const h of headings) {
    const doc = h.ownerDocument;
    const p = doc.createElement('p');
    const strong = doc.createElement('strong');
    while (h.firstChild !== null) strong.appendChild(h.firstChild);
    p.appendChild(strong);
    h.replaceWith(p);
  }
}

function walk(parent: Element): void {
  // Snapshot children first — we mutate `parent` as we go.
  const children = Array.from(parent.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      // Comment, CDATA, processing instruction — drop entirely.
      parent.removeChild(child);
      continue;
    }
    const el = child as Element;
    const tag = el.tagName;
    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown tag — UNWRAP it (replace the element with its
      // children) rather than collapsing to plain text, so any
      // allowed descendants the style-normalizer just injected
      // (`<strong>`, `<em>`, `<s>`, `<code>`, `<a>`) survive.
      walk(el);
      while (el.firstChild !== null) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      continue;
    }
    // Strip every attribute not in the allowlist.
    const allowed = ALLOWED_ATTRS[tag] ?? [];
    for (const attr of Array.from(el.attributes)) {
      if (!allowed.includes(attr.name)) {
        el.removeAttribute(attr.name);
      }
    }
    // `<a href>` extra check — reject `javascript:` etc.
    if (tag === 'A') {
      const href = el.getAttribute('href');
      if (href !== null && !isSafeHref(href)) {
        el.removeAttribute('href');
      }
    }
    walk(el);
  }
}

export function isSafeHref(href: string): boolean {
  const trimmed = href.trim().toLowerCase();
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('?')) {
    return true;
  }
  // Treat any scheme-less hrefs as relative.
  if (!trimmed.includes(':')) return true;
  for (const scheme of SAFE_HREF_SCHEMES) {
    if (trimmed.startsWith(scheme)) return true;
  }
  return false;
}

// URL pattern used by the smart-paste path. Matches:
//   - `http://` / `https://` URLs
//   - `www.` prefixed bare domains (autolinked as `https://`)
//   - `mailto:` addresses
// Deliberately conservative — we don't try to recognize every URL
// on the planet, just the common shapes that warrant auto-linking.
const URL_RE =
  /(?:\b(?:https?:\/\/|mailto:)[^\s<>"']+|\bwww\.[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\/[^\s<>"']*)?)/gi;

/**
 * True iff `s` is exactly a URL (no surrounding text). Used by the
 * paste handler to decide whether a clipboard payload should wrap a
 * live selection vs. inserting verbatim.
 */
export function isWholeUrl(s: string): boolean {
  const trimmed = s.trim();
  if (trimmed.length === 0) return false;
  URL_RE.lastIndex = 0;
  const m = URL_RE.exec(trimmed);
  return m !== null && m[0] === trimmed;
}

/**
 * Normalize a pasted URL into a value suitable for `href=`. `www.x.com`
 * picks up `https://`; `mailto:` and `http(s)://` pass through.
 */
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (/^tel:/i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
}

/**
 * Convert plain-text input into a DocumentFragment. Paragraph
 * breaks (one or more blank lines) become separate `<p>` blocks;
 * single newlines inside a paragraph become `<br>`. URLs inside
 * each line are auto-linked.
 *
 * Producing real `<p>` blocks (rather than one giant inline string
 * with `<br>`s) keeps the downstream block normalizer / serializer
 * happy and makes `hasNewLine` truthful even on huge pastes — the
 * composer's pill-vs-rounded-rect chrome flips correctly because
 * the markdown emit contains the `\n`s the structure implies.
 */
/**
 * Strip Markdown formatting markers from `text`, leaving the plain
 * prose. Used for the "paste as plain text" path (Cmd/Ctrl+Shift+V) so
 * a Markdown-formatted clipboard lands as unformatted characters
 * instead of being re-parsed into rich blocks. Mirrors the Element/
 * Matrix lexical composer's `stripMarkdownFormatting`:
 *   - `[label](url)` → `label` (or the bare URL when the label is empty)
 *   - `**b**` / `__b__` / `*i*` / `_i_` / `~~s~~` → inner text
 *   - `` `code` `` → `code`; fenced ```` ``` ```` lines dropped
 *   - leading `#` heading markers and `>` blockquote markers removed
 *   - 3+ consecutive blank lines collapsed to 2
 * Intentionally conservative — it only touches unambiguous leading /
 * paired markers so real prose containing `*` or `_` mid-word survives.
 */
export function stripMarkdownFormatting(text: string): string {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const out: string[] = [];
  for (const rawLine of lines) {
    let line = rawLine;
    // Drop code-fence delimiter lines entirely (keep the code body).
    if (/^\s*```/.test(line)) continue;
    // Leading block markers: heading hashes, blockquote chevrons.
    line = line.replace(/^\s{0,3}#{1,6}\s+/, '');
    line = line.replace(/^\s{0,3}>\s?/, '');
    // Links: `[label](url)` → label, or the URL when label is empty.
    line = line.replace(/\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (_m, label, url) =>
      (label as string).length > 0 ? (label as string) : (url as string),
    );
    // Paired inline emphasis / code. Order matters: bold before italic.
    // Underscore emphasis must be FLANKED by whitespace/punctuation so
    // intraword underscores (`snake_case`, `a_b_c`) survive — CommonMark's
    // intraword rule. Asterisk emphasis may be intraword, so stripping
    // `a*b*c` → `abc` is acceptable here.
    line = line.replace(/\*\*([^*]+)\*\*/g, '$1');
    line = line.replace(
      /(^|[\s.,!?;:()"'])__([^_]+?)__(?=$|[\s.,!?;:()"'])/g,
      '$1$2',
    );
    line = line.replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1$2');
    line = line.replace(
      /(^|[\s.,!?;:()"'])_([^_\s][^_]*?)_(?=$|[\s.,!?;:()"'])/g,
      '$1$2',
    );
    line = line.replace(/~~([^~]+)~~/g, '$1');
    line = line.replace(/`([^`]+)`/g, '$1');
    out.push(line);
  }
  // Collapse runs of 3+ blank lines down to 2.
  return out.join('\n').replace(/\n{3,}/g, '\n\n');
}

export function plainTextToFragment(
  text: string,
  doc: Document,
): DocumentFragment {
  const frag = doc.createDocumentFragment();
  // Normalize Windows line endings and split into paragraphs on
  // blank lines.
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const paragraphs = normalized.split(/\n{2,}/);
  for (const para of paragraphs) {
    if (para.length === 0) continue;
    const p = doc.createElement('p');
    const lines = para.split('\n');
    lines.forEach((line, idx) => {
      if (idx > 0) p.appendChild(doc.createElement('br'));
      appendLineWithAutolinks(line, doc, p);
    });
    if (p.childNodes.length === 0) p.appendChild(doc.createElement('br'));
    frag.appendChild(p);
  }
  return frag;
}

function appendLineWithAutolinks(
  line: string,
  doc: Document,
  target: DocumentFragment | HTMLElement,
): void {
  URL_RE.lastIndex = 0;
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = URL_RE.exec(line)) !== null) {
    const before = line.slice(cursor, m.index);
    if (before.length > 0) target.appendChild(doc.createTextNode(before));
    const a = doc.createElement('a');
    const href = normalizeUrl(m[0]);
    a.setAttribute('href', href);
    a.textContent = m[0];
    target.appendChild(a);
    cursor = m.index + m[0].length;
  }
  if (cursor < line.length) {
    target.appendChild(doc.createTextNode(line.slice(cursor)));
  }
}

/**
 * Walk a fragment of sanitized HTML and convert bare URLs in its
 * text nodes into `<a>` elements. Used after `sanitizeHtml` so the
 * autolink pass also applies to rich pastes that contain plain URLs
 * embedded in their text (e.g. "Visit https://example.com today").
 *
 * Existing `<a>` elements are left alone — we never re-link inside
 * a link.
 */
export function autolinkInPlace(root: ParentNode): void {
  const doc = (root as Node).ownerDocument ?? document;
  const walker = doc.createTreeWalker(root as Node, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      // Skip text inside <a>, <code>, <pre> — those should not be
      // re-linked.
      let cur: Node | null = node.parentNode;
      while (cur !== null && cur !== root) {
        if (cur.nodeType === Node.ELEMENT_NODE) {
          const tag = (cur as HTMLElement).tagName;
          if (tag === 'A' || tag === 'CODE' || tag === 'PRE') {
            return NodeFilter.FILTER_REJECT;
          }
        }
        cur = cur.parentNode;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const matches: Text[] = [];
  let n: Node | null = walker.nextNode();
  while (n !== null) {
    matches.push(n as Text);
    n = walker.nextNode();
  }
  for (const text of matches) {
    const value = text.nodeValue ?? '';
    URL_RE.lastIndex = 0;
    if (!URL_RE.test(value)) continue;
    URL_RE.lastIndex = 0;
    const frag = doc.createDocumentFragment();
    appendLineWithAutolinks(value, doc, frag);
    text.replaceWith(frag);
  }
}

/**
 * Count the line breaks a sanitized HTML payload *structurally*
 * implies — block elements (`P`/`DIV`/`LI`) and `<br>`s. Used to
 * detect the common failure where a code/IDE copy encodes its line
 * breaks as block LAYOUT of inline spans: once sanitized those
 * collapse and every newline vanishes, so the structural count comes
 * out far below the plain-text line count and the paste handler falls
 * back to the newline-preserving plain-text path.
 */
export function htmlStructuralLineCount(html: string): number {
  if (html.length === 0) return 0;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const blocks = doc.body.querySelectorAll('p, div, li, pre, blockquote');
  const brs = doc.body.querySelectorAll('br');
  // A single inline run with no block wrappers is still one line.
  return Math.max(1, blocks.length + brs.length);
}

/**
 * Heuristic: does this multi-line plain text look like source code?
 * Used so a pasted snippet becomes a code block (Slack-style) instead
 * of a wall of prose. Conservative — prose almost never trips it.
 */
export function looksLikeCode(text: string): boolean {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (lines.length < 2) return false;
  // "Strong" signals almost never appear in prose. Two or more lines with a
  // strong signal → treat as code regardless of the ratio, so short but
  // unambiguous snippets (a JSX block, a few statements) still paste as a
  // verbatim code block instead of losing their indentation to the
  // prose-paragraph fallback.
  const STRONG =
    /(=>|\bfunction\b|\bclass\b|\binterface\b|\bimport\s|\bexport\s|\breturn\b|className=|<\/[a-zA-Z]|\/>|;\s*$|\b(const|let|var)\s+[\w$]+\s*=|\$\{|::)/;
  let codey = 0;
  let strong = 0;
  for (const line of lines) {
    let isCode = false;
    if (/^\s{2,}\S/.test(line)) isCode = true; // indented
    if (/[;{}[\]()]\s*$/.test(line)) isCode = true; // ends in bracket/brace/semicolon
    if (/^\s*(\/\/|#|\/\*|\*|--)/.test(line)) isCode = true; // comment line
    if (
      /(=>|\bfunction\b|\bconst\b|\blet\b|\bvar\b|\bimport\b|\bexport\b|\breturn\b|\bclass\b|\binterface\b|\bif\s*\(|\bfor\s*\(|<\/?[a-zA-Z][\w-]*)/.test(
        line,
      )
    ) {
      isCode = true;
    }
    if (/className=|:\s*(string|number|boolean|void|any)\b|\$\{/.test(line)) {
      isCode = true;
    }
    if (isCode) codey++;
    if (STRONG.test(line)) strong++;
  }
  if (strong >= 2) return true;
  return codey >= Math.max(2, Math.ceil(lines.length * 0.4));
}

/**
 * Build a `<pre data-lang>…</pre>` fragment carrying `text` verbatim
 * (whitespace + newlines preserved), with its language auto-detected
 * and baked onto `data-lang` so the toolbar selector reflects it and
 * the wire fence carries it. The serializer emits this as a ``` block.
 */
export function buildCodeBlockFragment(
  text: string,
  doc: Document,
): DocumentFragment {
  const frag = doc.createDocumentFragment();
  const pre = doc.createElement('pre');
  const code = doc.createElement('code');
  const body = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  code.textContent = body;
  const lang = detectLanguage(body);
  if (lang !== null) pre.setAttribute('data-lang', lang);
  pre.appendChild(code);
  frag.appendChild(pre);
  return frag;
}

/**
 * Bake an auto-detected `data-lang` onto every `<pre>` in `root` that
 * doesn't already carry one — used after parsing pasted markdown that
 * contained ``` fences without explicit language labels, so each block
 * shows + serializes its own detected language.
 */
export function autodetectCodeBlocks(root: ParentNode): void {
  const pres = root.querySelectorAll('pre');
  pres.forEach((pre) => {
    if ((pre.getAttribute('data-lang') ?? '').length > 0) return;
    const code = pre.querySelector('code');
    const lang = detectLanguage(code?.textContent ?? pre.textContent ?? '');
    if (lang !== null) pre.setAttribute('data-lang', lang);
  });
}

/**
 * Classify a `ClipboardEvent` so the editor's paste handler can pick
 * the right branch without re-reading the clipboard twice.
 */
export interface PasteClassification {
  /** First image file on the clipboard, if any. */
  readonly image: File | null;
  /** Cleaned HTML payload (already sanitized). Empty when absent. */
  readonly html: string;
  /** Plain text payload. Empty when absent. */
  readonly text: string;
  /** `true` iff the text payload is exactly one URL. */
  readonly textIsUrl: boolean;
}

export function classifyClipboard(data: DataTransfer): PasteClassification {
  const image = pickImageFile(data);
  const html = data.getData('text/html') ?? '';
  const text = data.getData('text/plain') ?? '';
  return {
    image,
    html: html.length > 0 ? sanitizeHtml(html) : '',
    text,
    textIsUrl: text.length > 0 && isWholeUrl(text),
  };
}

function pickImageFile(data: DataTransfer): File | null {
  // Browsers don't agree on which of `items` / `files` carries
  // pasted image bytes. Chromium fills both for Cmd+V from a
  // browser; Safari fills `items` only for screenshot-tool paste;
  // some Firefox drops populate `files` only. Check both, in
  // order, returning the first image we find.
  const items = data.items;
  if (items !== null && items !== undefined) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item === undefined || item === null) continue;
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file !== null) return file;
      }
    }
  }
  const files = data.files;
  if (files !== null && files !== undefined) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file !== undefined && file.type.startsWith('image/')) {
        return file;
      }
    }
  }
  return null;
}
